import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Printer, ArrowLeft, CheckCircle2, Circle, MapPin, Ruler } from "lucide-react";
import { API, api } from "@/lib/api";
import { STAGE_META, PROPERTY_TYPE_LABELS, fmtPrice } from "@/lib/realestateConstants";
import { Button } from "@/components/ui/button";

const isDone = (item, property) => {
  const files = property.files_by_item?.[item.key] || [];
  if (item.type === "doc" || item.type === "docs") return files.length > 0;
  if (item.type === "text" || item.type === "link") return !!property.items?.[item.key]?.value?.trim();
  if (item.type === "form") return !!(property[item.key === "datos_vendedor" ? "seller" : "buyer"]?.name || "").trim();
  if (item.type === "visits") return (property.visits?.length || 0) > 0;
  return false;
};

export default function PropertyDossierPage() {
  const { id, propertyId } = useParams();
  const { data: ws } = useQuery({ queryKey: ["workspace", id], queryFn: async () => (await api.get(`/workspaces/${id}`)).data });
  const { data: tpl } = useQuery({ queryKey: ["realestate-template"], queryFn: async () => (await api.get("/realestate/template")).data, staleTime: Infinity });
  const { data: p } = useQuery({ queryKey: ["property", propertyId], queryFn: async () => (await api.get(`/properties/${propertyId}`)).data });
  if (!p || !tpl) return <p className="text-sm text-muted-foreground p-10 text-center">Preparando dossier…</p>;

  const photos = ["fotos_pro", "fotos_despues", "fotos_antes"].flatMap((k) => p.files_by_item?.[k] || []).filter((f) => f.category === "image").slice(0, 6);
  const meta = STAGE_META[p.stage];
  const stageIdx = ["preventa", "venta", "postventa", "vendido"].indexOf(p.stage);
  const visibleStages = tpl.workflow.filter((w) => ["preventa", "venta", "postventa"].indexOf(w.stage) <= Math.min(stageIdx, 2));
  const notes = ["feedback", "resumen_reunion", "precios_mercado"].map((k) => [k, p.items?.[k]?.value]).filter(([, v]) => v);

  return (
    <div className="min-h-screen bg-white text-[#171927]" data-testid="property-dossier">
      <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-2 print:hidden">
        <Button asChild size="sm" variant="ghost"><Link to={`/space/${id}/realestate/${propertyId}`} data-testid="dossier-back"><ArrowLeft size={15} className="mr-1" /> Volver a la ficha</Link></Button>
        <span className="flex-1" />
        <Button size="sm" onClick={() => window.print()} data-testid="dossier-print-btn"><Printer size={15} className="mr-1" /> Imprimir / Guardar PDF</Button>
      </div>

      <article className="max-w-3xl mx-auto px-8 pb-12 space-y-8 print:px-0">
        <div className="flex items-start justify-between gap-4 border-b pb-5">
          <div className="space-y-1">
            <p className="text-xs font-mono text-[#687083]">{ws?.name} · {p.ref} · {PROPERTY_TYPE_LABELS[p.property_type]}</p>
            <h1 className="font-heading text-3xl font-bold leading-tight">{p.title}</h1>
            <p className="text-sm text-[#687083] flex flex-wrap gap-x-4">
              {(p.address || p.city) && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {[p.address, p.city].filter(Boolean).join(", ")}</span>}
              {p.surface_m2 && <span className="inline-flex items-center gap-1"><Ruler size={13} /> {p.surface_m2} m²</span>}
              {p.catastro_ref && <span>RC {p.catastro_ref}</span>}
            </p>
          </div>
          <div className="text-right">
            <p className="font-heading text-3xl font-bold">{fmtPrice(p.price)}</p>
            <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-white" style={{ backgroundColor: meta.color }}>{meta.label}</span>
          </div>
        </div>

        {photos.length > 0 && (
          <section className={`grid gap-2 ${photos.length === 1 ? "grid-cols-1" : "grid-cols-3"}`} data-testid="dossier-photos">
            {photos.map((f, i) => (
              <img key={f.id} src={`${API}/files/${f.id}/download`} alt={f.name} className={`w-full object-cover rounded-xl ${i === 0 && photos.length > 1 ? "col-span-2 row-span-2 h-full max-h-[340px]" : "h-40"}`} />
            ))}
          </section>
        )}

        <section className="grid sm:grid-cols-2 gap-4 text-sm">
          <div className="rounded-xl bg-[#F7F5F1] p-4"><p className="text-[11px] uppercase tracking-wider text-[#687083]">Vendedor</p><p className="font-semibold">{p.seller?.name || "—"}</p><p className="text-xs text-[#687083]">{[p.seller?.phone, p.seller?.email].filter(Boolean).join(" · ")}</p></div>
          <div className="rounded-xl bg-[#F7F5F1] p-4"><p className="text-[11px] uppercase tracking-wider text-[#687083]">Comprador</p><p className="font-semibold">{p.buyer?.name || "Pendiente"}</p><p className="text-xs text-[#687083]">{[p.buyer?.phone, p.buyer?.email].filter(Boolean).join(" · ")}</p></div>
        </section>

        {notes.length > 0 && (
          <section className="space-y-2">
            <h2 className="font-heading text-lg font-bold">Notas de la operación</h2>
            {notes.map(([k, v]) => <div key={k} className="text-sm"><p className="text-[11px] uppercase tracking-wider text-[#687083]">{tpl.workflow[0].sections[0].items.find((i) => i.key === k)?.label}</p><p className="whitespace-pre-wrap">{v}</p></div>)}
          </section>
        )}

        <section className="space-y-4" data-testid="dossier-checklist">
          <h2 className="font-heading text-lg font-bold">Estado de la operación</h2>
          {visibleStages.map((st) => {
            const prog = p.progress?.[st.stage];
            return (
              <div key={st.stage} className="space-y-2 print:break-inside-avoid">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STAGE_META[st.stage].color }} />
                  <h3 className="font-semibold text-sm">{STAGE_META[st.stage].label}</h3>
                  <span className="text-xs text-[#687083]">{prog?.required_done}/{prog?.required_total} obligatorios</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                  {st.sections.flatMap((sec) => sec.items).map((it) => {
                    const ok = isDone(it, p);
                    return (
                      <div key={it.key} className="flex items-center gap-2 text-xs py-0.5">
                        {ok ? <CheckCircle2 size={14} className="text-emerald-600 shrink-0" /> : <Circle size={14} className="text-[#c9c5bc] shrink-0" />}
                        <span className={ok ? "" : "text-[#687083]"}>{it.label}</span>
                        {!it.required && <span className="text-[10px] text-[#9aa0ab]">(opcional)</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>

        {p.visits?.length > 0 && (
          <section className="space-y-2 print:break-inside-avoid">
            <h2 className="font-heading text-lg font-bold">Visitas realizadas ({p.visits.length})</h2>
            <table className="w-full text-xs">
              <tbody>{p.visits.map((v) => <tr key={v.id} className="border-b"><td className="py-1">{new Date(v.date + "T00:00:00").toLocaleDateString()} {v.time || ""}</td><td className="py-1">{v.visitor_name}</td><td className="py-1 text-[#687083]">{v.notes}</td></tr>)}</tbody>
            </table>
          </section>
        )}

        <footer className="border-t pt-4 text-[11px] text-[#9aa0ab] flex justify-between">
          <span>Dossier generado con nuxxum · {new Date().toLocaleDateString()}</span><span>nuxxum · Todo en su sitio. Todos conectados.</span>
        </footer>
      </article>
    </div>
  );
}
